<template>
    <IPNav
        ref="navBarRef"
        class="text-[14px]"
        :navModuleType="NAV_MENU_TYPE.NEW_TASKS"
        :ipNavService="ipNavService"
    />
</template>

<script setup lang="ts">
import { IPNav, NAV_MENU_TYPE, createIpNavService } from '@eureka/shared/src/components/ip-nav';
import { headerService } from '@/services/header';
import { useRouter } from 'vue-router';
import { watch, ref, onMounted, onUnmounted } from 'vue';
import psHttp from '@patsnap/common-ps-http';
import { useNavBar } from '@/composables/useNavBar';
import { useLoginSdk } from '@/composables/useLoginSdk';
import { usePlan } from '@eureka/shared/src/login-sdk';
import { EUREKA_SERVICE_URI } from '@/constants/base-url';
import { useLastProjectInfo } from '@/composables/useLastProjectInfo';
import { track } from '@eureka/shared/src/utils/track';

const navBarRef = ref<any>();
const { value: plan } = usePlan();
const { currentReturnAgentType } = useLastProjectInfo();

const { openLoginDialog } = useLoginSdk();

function openLogin() {
    openLoginDialog(false);
}

const router = useRouter();
const { isCollapsed } = useNavBar();

// 获取所有路由的 name 集合
const routeNames = new Set(
    router.getRoutes()
        .map(route => route.name)
        .filter(name => name) // 过滤掉没有 name 的路由
);

// 封装 psHttp 为符合 IAxiosClient 接口的对象
const httpClient = {
    get: (config: { url: string; params?: any; [key: string]: any }) =>
        psHttp({ ...config, method: 'GET' }).then(res => res.data),
    post: (config: { url: string; data?: any; [key: string]: any }) =>
        psHttp({ ...config, method: 'POST' }).then(res => res.data),
    delete: (config: { url: string; [key: string]: any }) =>
        psHttp({ ...config, method: 'DELETE' }).then(res => res.data),
};

const ipNavService = createIpNavService({
    headerService: headerService as any,
    httpClient,
    apiBaseUrl: EUREKA_SERVICE_URI,
    responsive: false,
    isActive: (context) => {
        const path = context.routePath;

        // 从 router 配置中查找匹配的路由,返回其 name
        const matchedRoute = router.getRoutes().find(route => {
            // 精确匹配
            if (route.path === path) {
                return true;
            }
            // 路径包含匹配(用于子路由)
            if (path.startsWith(route.path) && route.path !== '/') {
                return true;
            }
            return false;
        });

        return matchedRoute?.name as string || null;
    },
    onNavigate: (context) => {
        if (context.menu.type === NAV_MENU_TYPE.NEW_TASKS as string) {
            if(currentReturnAgentType.value) {
                context.preventDefault();
                router.push({
                    path: '/',
                    query: { agent: currentReturnAgentType.value },
                });
            } else {
                context.preventDefault();
                location.href = '/';
            }
            return;
        }

        // 如果菜单的 type 属于项目路由名称,则阻止默认跳转,使用项目路由
        if (context.menu?.type && routeNames.has(context.menu.type)) {
            context.preventDefault();
            router.push({ name: context.menu.type });
        }
    },
    isShowUpgrade() {
        const isCn = headerService?.account?.siteRegion === 'CN';
        return !!(plan.value?.isFreemium && plan.value?.isPersonal) && !isCn;
    },
    onLogin: () => {
        openLogin();
    },
    onUpgrade: () => {
        track({
            event_type: 'SS Eureka Upgrade Page',
            source_type: 'upgrade_trigger',
            event_detail_str: JSON.stringify({
                product_line: 'eureka_ip',
                trigger_source: 'side_nav',
            }),
        });

        router.push({ name: 'Upgrade', query: { from_trigger: 'side_nav' } });
    },
});

watch(isCollapsed, (collapsed) => {
    ipNavService.toggleSidebar(!collapsed);
}, { immediate: true });
</script>

<style scoped>
/* IP-Nav 组件自带样式,通常不需要额外样式 */
</style>
